import { isEuDomiciled } from '@crm/shared';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { OpportunityEditModal } from '../components/opportunities/OpportunityEditModal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { TrashIcon } from '../components/ui/icons';
import { apiClient, type Activity, type Opportunity, type OpportunityFull } from '../lib/api';
import { formatBps, formatChf, formatChfCompact } from '../lib/format';

const ACTIVITY_TYPE_LABEL: Record<string, string> = {
  visit: 'Visit',
  call: 'Phone call',
  email: 'Email',
  meeting: 'Meeting',
  note: 'Note',
};

const STAGE_LABEL: Record<string, string> = {
  identified: 'Identified',
  qualifying: 'Qualifying',
  compliance_review: 'Compliance review',
  proposal: 'Proposal',
  onboarding: 'Onboarding',
  active: 'Active',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function OpportunityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [opp, setOpp] = useState<OpportunityFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showActivityPanel, setShowActivityPanel] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function performDelete() {
    if (!opp) return;
    setDeleting(true);
    try {
      await apiClient.deleteOpportunity(opp.id);
      navigate('/pipeline');
    } catch (e) {
      setDeleting(false);
      setConfirmOpen(false);
      setError((e as Error).message);
    }
  }

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    apiClient
      .getOpportunityFull(id)
      .then((data) => {
        if (!cancelled) setOpp(data);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const effectiveProbability = useMemo(() => {
    if (!opp) return 0;
    return opp.stage_probability_override ?? opp.stage_probability_pct;
  }, [opp]);

  if (loading) {
    return (
      <div className="px-8 py-12 max-w-content mx-auto">
        <div className="h-6 w-32 bg-platinum rounded animate-pulse" />
        <div className="h-12 w-80 bg-platinum rounded animate-pulse mt-3" />
        <div className="mt-10 h-40 card animate-pulse bg-platinum/40" />
      </div>
    );
  }

  if (error || !opp) {
    return (
      <div className="px-8 py-12 max-w-content mx-auto">
        <p className="label-uppercase">— Error</p>
        <h1 className="mt-2 font-display text-3xl text-graphite">Unable to load opportunity</h1>
        <p className="mt-3 text-ink-secondary text-sm">{error ?? 'Not found.'}</p>
        <Link to="/pipeline" className="btn-secondary mt-6 inline-flex">
          Back to pipeline
        </Link>
      </div>
    );
  }

  const showEuReverseWarning =
    isEuDomiciled(opp.domiciled_country) &&
    ['compliance_review', 'proposal', 'onboarding'].includes(opp.stage) &&
    !opp.reverse_solicitation_evidence;

  return (
    <div className="px-8 py-10 max-w-content mx-auto">
      <Link
        to="/pipeline"
        className="text-xs uppercase tracking-wider text-ink-tertiary hover:text-ink"
      >
        ← Pipeline
      </Link>

      <header className="mt-4">
        <p className="label-uppercase">— Opportunity · {STAGE_LABEL[opp.stage]}</p>
        <div className="mt-3 flex items-end justify-between gap-6 flex-wrap">
          <h1 className="font-display text-4xl text-graphite">{opp.name}</h1>
          <div className="flex items-center gap-2">
            <button type="button" className="btn-secondary" onClick={() => setEditing(true)}>
              Edit
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => setShowActivityPanel(true)}
            >
              Log activity
            </button>
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={deleting}
              className="btn-icon-danger disabled:opacity-50"
              aria-label="Delete opportunity"
              title="Delete opportunity"
            >
              <TrashIcon />
            </button>
          </div>
        </div>
        <p className="mt-2 text-sm text-ink-secondary">
          {opp.industry_sector} · {opp.domiciled_country} · {opp.source}
          {opp.referred_by && <> · referred by {opp.referred_by}</>}
        </p>
      </header>

      {showEuReverseWarning && (
        <div className="mt-6 card !bg-bg-alert border-signal/30 p-5">
          <p className="label-uppercase !text-signal">— MiFID II</p>
          <p className="mt-2 text-sm text-graphite leading-relaxed">
            EU-domiciled lead ({opp.domiciled_country}) in {STAGE_LABEL[opp.stage]} without
            documented evidence of reverse solicitation. Fill this in to stay within the regulatory
            framework.
          </p>
        </div>
      )}

      <section className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Kpi
          label="Initial expected"
          value={formatChf(opp.initial_expected_amount)}
          sub={`Potential Y+1 · ${formatChfCompact(opp.potential_expected_amount_y1)}`}
        />
        <Kpi
          label="RoA"
          value={formatBps(opp.roa_bps)}
          sub={`Score · ${formatChfCompact(opp.score_chf)}`}
        />
        <Kpi
          label="Probability"
          value={`${effectiveProbability.toFixed(0)}%`}
          sub={
            opp.stage_probability_override != null
              ? 'Manual override'
              : `Stage default · ${opp.stage_probability_pct.toFixed(0)}%`
          }
        />
      </section>

      <section className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <article className="card p-6">
            <p className="label-uppercase">— Snapshot</p>
            <dl className="mt-4 grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
              <Pair label="Existing bank" value={opp.existing_bank ?? '—'} />
              <Pair label="Target bank" value={opp.target_bank?.name ?? '—'} />
              <Pair label="Total wealth" value={formatChf(opp.total_wealth)} />
              <Pair label="Estimated annual RoA" value={formatChf(opp.estimated_roa_chf)} />
              <Pair label="Expected timeframe" value={opp.expected_timeframe ?? '—'} />
              <Pair label="KYC status" value={opp.kyc_status} />
              <Pair label="PEP flag" value={opp.pep_flag ? 'Yes — review needed' : 'No'} />
              <Pair label="Owner RM" value={opp.owner_rm?.display_name ?? '—'} />
            </dl>
          </article>

          <article className="card p-6">
            <p className="label-uppercase">— Activity timeline</p>
            {opp.activities.length === 0 ? (
              <p className="mt-4 text-sm italic text-ink-tertiary">No activities logged yet.</p>
            ) : (
              <ol className="mt-5 space-y-5 border-l border-platinum pl-5">
                {opp.activities.map((a) => (
                  <li key={a.id}>
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="text-sm text-ink">
                        <span className="font-medium">{ACTIVITY_TYPE_LABEL[a.type]}</span>
                        {a.counterparty && (
                          <span className="text-ink-secondary"> · with {a.counterparty}</span>
                        )}
                      </p>
                      <p className="text-xs font-tabular text-ink-tertiary shrink-0">
                        {formatDateTime(a.activity_date)}
                      </p>
                    </div>
                    {a.summary && (
                      <p className="mt-1 text-sm text-ink-secondary leading-relaxed">{a.summary}</p>
                    )}
                    {a.creator && (
                      <p className="mt-1 text-xs text-ink-tertiary">
                        logged by {a.creator.display_name}
                      </p>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </article>

          {opp.client && opp.client.portfolios.length > 0 && (
            <article className="card p-6">
              <p className="label-uppercase">— Portfolio (AMOne mirror)</p>
              <p className="mt-2 text-xs text-ink-tertiary">
                Onboarded {formatDate(opp.client.onboarded_at)}. Read-only.
              </p>
              <ul className="mt-5 space-y-4">
                {opp.client.portfolios.map((p) => (
                  <li
                    key={p.id}
                    className="border-t border-platinum pt-3 first:border-t-0 first:pt-0"
                  >
                    <div className="flex items-baseline justify-between">
                      <p className="text-sm font-medium text-ink">{p.custodian.name}</p>
                      <p className="text-sm font-tabular text-ink">{formatChf(p.aum_chf)}</p>
                    </div>
                    <p className="mt-1 text-xs text-ink-tertiary font-tabular">
                      Equities {p.asset_allocation.equities ?? 0}% · Bonds{' '}
                      {p.asset_allocation.bonds ?? 0}% · Alt {p.asset_allocation.alternatives ?? 0}%
                      · Cash {p.asset_allocation.cash ?? 0}%
                    </p>
                  </li>
                ))}
              </ul>
            </article>
          )}
        </div>

        <aside>
          <article className="card p-6">
            <p className="label-uppercase">— Stage history</p>
            {opp.status_history.length === 0 ? (
              <p className="mt-4 text-sm italic text-ink-tertiary">No transitions recorded.</p>
            ) : (
              <ol className="mt-5 space-y-4">
                {opp.status_history.map((h) => (
                  <li key={h.id} className="text-sm">
                    <p className="text-ink">
                      <span className="text-ink-tertiary">
                        {h.from_stage ? STAGE_LABEL[h.from_stage] : 'Created'}
                      </span>{' '}
                      → <span className="font-medium">{STAGE_LABEL[h.to_stage]}</span>
                    </p>
                    <p className="text-xs font-tabular text-ink-tertiary mt-0.5">
                      {formatDateTime(h.changed_at)}
                      {h.user && <> · {h.user.display_name}</>}
                    </p>
                    {h.override_probability != null && (
                      <p className="mt-1 text-xs text-ink-secondary italic">
                        Override {h.override_probability.toFixed(0)}%
                        {h.override_reason && <> — {h.override_reason}</>}
                      </p>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </article>
        </aside>
      </section>

      {showActivityPanel && (
        <LogActivityPanel
          opportunityId={opp.id}
          onClose={() => setShowActivityPanel(false)}
          onCreated={(a) => {
            setOpp((prev) => (prev ? { ...prev, activities: [a, ...prev.activities] } : prev));
            setShowActivityPanel(false);
          }}
        />
      )}

      <OpportunityEditModal
        open={editing}
        opportunity={opp}
        onClose={() => setEditing(false)}
        onSaved={(updated: Opportunity) => {
          setEditing(false);
          // Merge the patched columns back into the full opportunity so the
          // detail page reflects the edit without a full reload.
          setOpp((prev) => (prev ? { ...prev, ...updated } : prev));
        }}
      />

      <ConfirmDialog
        open={confirmOpen}
        variant="danger"
        title={`Delete "${opp.name}"?`}
        body={
          <>
            <p>
              This will permanently delete this opportunity from the pipeline. Any logged activities
              are kept for audit, but the row will disappear from every list.
            </p>
            <p className="mt-2 text-ink-tertiary">This cannot be undone.</p>
          </>
        }
        confirmLabel="Delete opportunity"
        busy={deleting}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={performDelete}
      />
    </div>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="card p-5">
      <p className="label-uppercase">— {label}</p>
      <p className="mt-2 font-display text-3xl text-graphite leading-none">{value}</p>
      <p className="mt-2 text-xs text-ink-tertiary font-tabular">{sub}</p>
    </div>
  );
}

function Pair({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-ink-tertiary text-xs uppercase tracking-wide">{label}</dt>
      <dd className="text-ink text-sm">{value}</dd>
    </>
  );
}

function LogActivityPanel({
  opportunityId,
  onClose,
  onCreated,
}: {
  opportunityId: string;
  onClose: () => void;
  onCreated: (a: Activity) => void;
}) {
  const [type, setType] = useState<'visit' | 'call' | 'email' | 'meeting' | 'note'>('call');
  const [counterparty, setCounterparty] = useState('');
  const [duration, setDuration] = useState('');
  const [summary, setSummary] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const a = await apiClient.createActivity(opportunityId, {
        type,
        activity_date: new Date().toISOString(),
        ...(counterparty ? { counterparty } : {}),
        ...(duration ? { duration_minutes: Number(duration) } : {}),
        ...(summary ? { summary } : {}),
      });
      onCreated(a);
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  }

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-graphite/20" onClick={onClose} />
      <aside className="w-[460px] max-w-full bg-surface shadow-panel h-full overflow-y-auto p-8 flex flex-col">
        <p className="label-uppercase">— Log activity</p>
        <h2 className="mt-3 font-display text-2xl font-semibold text-graphite leading-snug">
          Nouvelle interaction
        </h2>

        <form onSubmit={submit} className="mt-6 flex-1 flex flex-col gap-5">
          <div>
            <label className="block text-xs uppercase tracking-wider text-ink-secondary mb-2">
              Type
            </label>
            <div className="grid grid-cols-5 gap-1">
              {(['visit', 'call', 'email', 'meeting', 'note'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  className={
                    'py-2 text-xs uppercase tracking-wider rounded-card border transition-colors ' +
                    (type === t
                      ? 'bg-graphite text-white border-graphite'
                      : 'bg-surface text-ink-secondary border-platinum hover:border-ink/40')
                  }
                  onClick={() => setType(t)}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label
              htmlFor="counterparty"
              className="block text-xs uppercase tracking-wider text-ink-secondary mb-2"
            >
              Counterparty
            </label>
            <input
              id="counterparty"
              className="w-full px-3 py-2 border-b border-platinum bg-transparent text-sm focus:outline-none focus:border-graphite"
              value={counterparty}
              onChange={(e) => setCounterparty(e.target.value)}
              placeholder="Name (PII — logged in audit trail)"
              maxLength={200}
            />
          </div>

          <div>
            <label
              htmlFor="duration"
              className="block text-xs uppercase tracking-wider text-ink-secondary mb-2"
            >
              Duration · minutes
            </label>
            <input
              id="duration"
              type="number"
              min={0}
              className="w-full px-3 py-2 border-b border-platinum bg-transparent text-sm focus:outline-none focus:border-graphite font-tabular"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
            />
          </div>

          <div>
            <label
              htmlFor="summary"
              className="block text-xs uppercase tracking-wider text-ink-secondary mb-2"
            >
              Summary
            </label>
            <textarea
              id="summary"
              className="w-full px-3 py-2 border border-platinum rounded-card bg-transparent text-sm focus:outline-none focus:border-graphite min-h-[120px]"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              maxLength={2000}
              placeholder="Key points discussed, next steps…"
            />
          </div>

          {error && <p className="text-sm text-semantic-error">{error}</p>}

          <div className="mt-auto flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" disabled={submitting} className="btn-primary flex-1">
              {submitting ? 'Saving…' : 'Save activity'}
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}
